import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/axios';
import remountOnId from '../utils/remountOnId';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  Star, 
  DollarSign, 
  MessageSquare, 
  Award,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { canEditAppraisal } from '../utils/compensationPermissions';
import { formatRaise } from '../utils/currency';
import { formatDateOnly } from '../utils/dateUtils';

const LONG_DATE = { year: 'numeric', month: 'long', day: 'numeric' };

const AppraisalDetail = () => {
  const { id } = useParams();
  const { hasPermission, user } = useAuth();
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfReview, setSelfReview] = useState('');
  const [savingSelfReview, setSavingSelfReview] = useState(false);

  useEffect(() => {
    // Only the latest run may populate the page: a stale response (StrictMode's double
    // run in dev, or an id change) would otherwise land after the user started typing.
    let cancelled = false;
    const fetchAppraisal = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/appraisals/${id}`);
        if (cancelled) return;
        setAppraisal(res.data.data);
        setSelfReview(res.data.data?.employee_comments || '');
      } catch (err) {
        if (!cancelled) toast.error('Failed to load appraisal');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAppraisal();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isOwnAppraisal = !!user?.employee_id && appraisal?.employee_id === user.employee_id;
  // Full edit (ratings/manager_comments/salary raise) is for HR/managers only, and never
  // on a manager's own appraisal. Employees with appraisals:update_own get a self-review
  // textarea below instead.
  const canEdit = canEditAppraisal(hasPermission, user, appraisal);
  const canSelfReview = isOwnAppraisal && hasPermission('appraisals', 'update_own');

  const handleSaveSelfReview = async () => {
    setSavingSelfReview(true);
    try {
      await api.put(`/api/appraisals/${id}/employee-comments`, { employee_comments: selfReview });
      setAppraisal(prev => prev ? { ...prev, employee_comments: selfReview } : prev);
      toast.success('Self-review saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save self-review');
    } finally {
      setSavingSelfReview(false);
    }
  };

  const getRatingIcon = (rating) => {
    if (!rating) return <AlertCircle className="h-5 w-5 text-gray-400" />;
    return <Star className="h-5 w-5 text-yellow-500 fill-current" />;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'overdue':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!appraisal) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <FileText className="h-12 w-12" />
          </div>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Appraisal not found</h3>
          <p className="mt-1 text-sm text-gray-500">The appraisal you're looking for doesn't exist.</p>
          <div className="mt-6">
            <Link
              to="/appraisals"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-100 hover:bg-primary-200 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Appraisals
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/appraisals"
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Appraisal Details</h1>
              <p className="text-sm text-gray-500 mt-1">
                Review and manage employee performance evaluation
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <StatusBadge status={appraisal.status} type="appraisal" />
            {getStatusIcon(appraisal.status)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee Information Card */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2 text-primary-600" />
                Employee Information
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {appraisal.first_name} {appraisal.last_name}
                  </h3>
                  <p className="text-sm text-gray-500">Employee ID: {appraisal.emp_id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Appraisal Details Card */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Award className="h-5 w-5 mr-2 text-primary-600" />
                Appraisal Details
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Appraisal Date</p>
                      <p className="text-sm text-gray-900">
                        {formatDateOnly(appraisal.appraisal_date, 'en-US', LONG_DATE)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Next Appraisal Date</p>
                      <p className="text-sm text-gray-900">
                        {appraisal.next_appraisal_date
                          ? formatDateOnly(appraisal.next_appraisal_date, 'en-US', LONG_DATE)
                          : 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                                       <div className="flex items-center space-x-3">
                       <Star className="h-5 w-5 text-gray-400" />
                       <div>
                         <p className="text-sm font-medium text-gray-500">Rating</p>
                         <div className="flex items-center space-x-2">
                           {getRatingIcon(appraisal.overall_rating)}
                           <span className="text-sm text-gray-900">
                             {appraisal.overall_rating ? `${appraisal.overall_rating}/5` : 'Not rated'}
                           </span>
                         </div>
                       </div>
                     </div>
                  
                  <div className="flex items-center space-x-3">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Raise Amount</p>
                      <p className="text-sm text-gray-900">
                        {formatRaise(appraisal.raise_amount, appraisal.currency, 'No raise given')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Summary Card */}
          {appraisal.performance_summary && (
            <div className="bg-white rounded-lg shadow-soft border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary-600" />
                  Performance Summary
                </h2>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {appraisal.performance_summary}
                </p>
              </div>
            </div>
          )}

          {/* Goals and Areas for Improvement */}
          {(appraisal.goals_achieved || appraisal.areas_for_improvement) && (
            <div className="bg-white rounded-lg shadow-soft border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-primary-600" />
                  Goals & Development
                </h2>
              </div>
              <div className="px-6 py-4 space-y-6">
                {appraisal.goals_achieved && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Goals Achieved</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {appraisal.goals_achieved}
                    </p>
                  </div>
                )}
                
                {appraisal.areas_for_improvement && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Areas for Improvement</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {appraisal.areas_for_improvement}
                    </p>
                  </div>
                )}
                
                {appraisal.recommendations && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Recommendations</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {appraisal.recommendations}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Comments Card */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-primary-600" />
                Comments
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {appraisal.manager_comments && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Manager Comments</h3>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {appraisal.manager_comments}
                    </p>
                  </div>
                </div>
              )}
              
              {canSelfReview ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Your Self-Review</h3>
                  <textarea
                    value={selfReview}
                    onChange={(e) => setSelfReview(e.target.value)}
                    rows={5}
                    placeholder="Share your reflections on this appraisal period..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleSaveSelfReview}
                      disabled={savingSelfReview || selfReview === (appraisal.employee_comments || '')}
                      className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingSelfReview ? 'Saving...' : 'Save Self-Review'}
                    </button>
                  </div>
                </div>
              ) : appraisal.employee_comments && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Employee Self-Review</h3>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {appraisal.employee_comments}
                    </p>
                  </div>
                </div>
              )}

              {!canSelfReview && !appraisal.manager_comments && !appraisal.employee_comments && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No comments available
                </p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="px-6 py-4 space-y-3">
              <Link
                to={canEdit ? `/appraisals/${id}/edit` : '#'}
                data-testid="appraisal-detail-edit"
                onClick={(e) => { if (!canEdit) e.preventDefault(); }}
                aria-disabled={!canEdit}
                title={canEdit ? 'Edit Appraisal' : 'You do not have permission to edit appraisals'}
                className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors ${
                  canEdit
                    ? 'text-white bg-primary-600 hover:bg-primary-700'
                    : 'text-gray-500 bg-gray-200 cursor-not-allowed'
                }`}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Appraisal
              </Link>
              <Link
                to="/appraisals"
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// One instance per record: see utils/remountOnId.js.
export default remountOnId(AppraisalDetail);
